import classnames from 'classnames2';
import { capitalize } from 'lodash-es';
import React from 'react';

import { Icon } from '../../Icon';
import { InputSizes, ValidationStates } from '../enums';

import styles from '../BaseInput/BaseInput.module.scss';

export type OnChangeCallback = (newValue: number) => void;

export interface IParsedInputProps {
  inputClassName?: string;
  className?: string;
  size?: InputSizes; // Move towards using size through a prop instead of a namespace
  dataTest?: string;
  disabled?: boolean;
  id?: string;
  maxLength?: number;
  minLength?: number;
  onChange?: OnChangeCallback;
  placeholder?: string;
  required?: boolean;
  validationState?: ValidationStates;
  value?: string | number;
  defaultValue?: string | number;
  min?: string | number;
  max?: string | number;
  onBlur?(event): void;
  onFocus?(event): void;
  onKeyDown?(event: React.KeyboardEvent<HTMLInputElement>): void;
  onKeyUp?(event: React.KeyboardEvent<HTMLInputElement>): void;
}

export interface IParsedInputState {
  invalid: boolean;
  rawInput: string;
  numericInput: number;
  containerStyle: any;
}

export class ParsedNumberInput extends React.Component<IParsedInputProps, IParsedInputState> {
  static displayName = 'Input.ParsedNumberInput';

  constructor(props) {
    super(props);

    const inputSize = capitalize(props.size) || InputSizes.Default;

    const containerStyle = classnames(styles.container, {
      [styles.defaultSize]: inputSize === InputSizes.Default,
      [styles.smallSize]: inputSize === InputSizes.Small,
      [styles.largeSize]: inputSize === InputSizes.Large,
      [styles.successState]: props.validationState === ValidationStates.Success,
      [styles.errorState]: props.validationState === ValidationStates.Error,
      [styles.disabledState]: props.disabled
    });

    this.state = {
      invalid: props.validationState && props.validationState === ValidationStates.Error,
      rawInput: props.value || '',
      numericInput: null,
      containerStyle: containerStyle
    };
  }

  createChangeHandler = (callback: OnChangeCallback) =>
    callback &&
    ((e) => {
      const parsed = parseFloat(e.target.value.replace(/[$¢€,]+/g, ''));
      this.setState({
        rawInput: e.target.value,
        numericInput: parsed,
        invalid: (this.props.validationState && this.props.validationState === ValidationStates.Error) || isNaN(parsed)
      });
      callback(parsed);
    });

  render() {
    const { containerStyle, invalid } = this.state;

    let inputProps = {};

    if (this.props.value !== undefined && this.props.value !== null && this.props.value !== '') {
      inputProps = Object.assign(inputProps, { value: this.props.value });
    }

    if (this.props.defaultValue) {
      inputProps = Object.assign(inputProps, { defaultValue: this.props.defaultValue });
    }

    return (
      <div className={classnames(containerStyle, this.props.className)}>
        <input
          className={classnames(styles.input, this.props.inputClassName)}
          data-test={this.props.dataTest}
          disabled={this.props.disabled}
          id={this.props.id}
          maxLength={this.props.maxLength}
          minLength={this.props.minLength}
          onBlur={this.createChangeHandler(this.props.onBlur)}
          onChange={this.createChangeHandler(this.props.onChange)}
          onFocus={this.props.onFocus}
          onKeyDown={this.props.onKeyDown}
          onKeyUp={this.props.onKeyUp}
          placeholder={this.props.placeholder}
          required={this.props.required}
          type="text"
          min={this.props.min}
          max={this.props.max}
          {...inputProps}
        />
        <span className={styles.iconWrapper}>
          {this.props.validationState === ValidationStates.Success && (
            <Icon className={styles.icon} icon="status-success" />
          )}
          {invalid && <Icon className={styles.icon} icon="status-error" />}
        </span>
      </div>
    );
  }
}
